import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteHeader, PBOLogo } from "@/components/Logo";
import { api, fetchDraft, joinDraft } from "@/lib/api";
import { saveSession, getSession } from "@/lib/storage";

export default function JoinDraft() {
  const { code: codeParam } = useParams();
  const nav = useNavigate();
  const [code, setCode] = useState((codeParam || "").toUpperCase());
  const [username, setUsername] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  // Auto-lookup if code present. A code can belong to an auction draft OR a
  // salary-cap contest — redirect to the contest join flow for the latter.
  useEffect(() => {
    if (code.length >= 4) {
      api
        .get(`/lookup/${code}`)
        .then(({ data }) => {
          if (data.kind === "contest") {
            toast.info("That code is a salary-cap contest — taking you there");
            nav(`/contest/join/${code}`, { replace: true });
            return null;
          }
          return fetchDraft(code);
        })
        .then((d) => d && setDraft(d))
        .catch(() => setDraft(null));
    } else {
      setDraft(null);
    }
  }, [code, nav]);

  // If we already have a session for this code, jump in directly
  useEffect(() => {
    if (codeParam) {
      const s = getSession(codeParam);
      if (s) nav(`/draft/${codeParam.toUpperCase()}`, { replace: true });
    }
  }, [codeParam, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Pick a display name");
      return;
    }
    if (code.length < 4) {
      toast.error("Enter the 4-letter room code");
      return;
    }
    setBusy(true);
    try {
      const res = await joinDraft(code, username.trim());
      saveSession(res.code, res.participant_id, username.trim());
      if (res.rejoined) {
        toast.success(`Welcome back, ${username.trim()}`);
      } else {
        toast.success(`Joined ${res.code}`);
      }
      nav(`/draft/${res.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join draft");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-pbo-cream">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-start mb-10">
          <PBOLogo size={96} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
              Join a draft
            </div>
            <h1 className="font-serif text-pbo-green text-4xl sm:text-5xl mt-2 leading-tight">
              Pull up a chair at the clubhouse.
            </h1>
            <p className="text-pbo-ink/65 mt-3 text-sm">
              Enter your draft code and pick a display name. No accounts, just bids.
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white border border-pbo-border rounded-2xl p-6 sm:p-8 space-y-6"
        >
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
              Room code
            </Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
              maxLength={6}
              placeholder="ABCD"
              className="font-mono text-3xl tracking-[0.4em] h-16 text-center text-pbo-green bg-pbo-paper"
              data-testid="join-code-input"
            />
            {code.length >= 4 && (
              <div className="text-xs mt-1">
                {draft ? (
                  <span className="text-pbo-green-soft">
                    ✓ Draft: <strong>{draft.draft.name}</strong> ·{" "}
                    {draft.participants.length} in the room ·{" "}
                    {draft.draft.status === "lobby"
                      ? "Lobby open"
                      : draft.draft.status === "active"
                      ? "In progress — you'll join as a spectator"
                      : "Complete — join to view the recap"}
                  </span>
                ) : (
                  <span className="text-pbo-muted">Looking up code…</span>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
              Your display name
            </Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value.slice(0, 30))}
              placeholder="e.g. Spieth's caddie"
              className="h-12 bg-pbo-paper"
              data-testid="join-username-input"
            />
            <p className="text-[11px] text-pbo-muted">
              Coming back from another browser or device? Use the same display name to pick up where you left off.
            </p>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-14 rounded-full bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep font-semibold text-base"
            data-testid="submit-join-btn"
          >
            {busy ? "Joining..." : "Enter draft room"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}
